import React, { PropsWithChildren } from "react";
import rcss from '../../Styles/reports.module.css';
import { enhancedContentSourceLocation } from "../../lib/ContentSource";
import { hasCategoryAndEventName, TabEvent } from "../../lib/Events";
import { DbRecordedEvent, ISODateTimeString } from "../../State/EvidenceDB/DatabaseTypes";
import { describeEncodedSearchTermUsingFunctionNotationHtml } from "../../lib/Search/EncodedSearchTerm";
import { ChronologicalReportingEvent, isDbNetworkEventWithSearchResults } from "../../workers/NetworkEventDbSearchWebWorkerStructures";
import { InstrumentationEventDomEventFired } from "../../lib/Events/InstrumentationEvent";
import { getRegisteredDomain } from "../../lib/utils/domains";
import { InteractiveRequestId, InteractiveRequestIdBaseProps } from "./InteractiveRequestId";
import { ContentSourceSearchResultKey, EventSearchResults } from "../../lib/searchNetworkEvent";
import { observer } from "mobx-react-lite";
import { ChronologicalResultsObserver } from "./ChronologicalResultsObserver";
import { GlobalContext } from "./GlobalContext";
import { Literal } from "../../Styles/ReportStyles";
import { HasLicensePermissions } from "../../State/EvidenceDB/LicensePermissions";
import { UnlicensedText } from "../../constants";

const isDomEventFiredEvent = hasCategoryAndEventName('Instrumentation','DomEventFired');

const QuotedLiteral = (props: PropsWithChildren<{}>) => {
	return <>&ldquo;<Literal>{props.children}</Literal>&rdquo;</>;
}

export const MsSinceRecordingStartView = ({msSinceRecordingStart}: {msSinceRecordingStart: number}) => {
	const ms = Math.floor(msSinceRecordingStart % 1000);
	const seconds = Math.floor(msSinceRecordingStart / 1000) % 60;
	const minutes = Math.floor(msSinceRecordingStart / 60000) % 60;
	const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
	return <span className={rcss.timeStampElement}>{timeStr}</span>;
}

export const SearchResultEventResultView = observer( ({
	result, msSinceRecordingStart, licensePermissions, ...rest
}: InteractiveRequestIdBaseProps & HasLicensePermissions & {
	result: EventSearchResults;	
	msSinceRecordingStart: number;}
) => {
	const {requestId, eventIndex, searchResults} = result;
	const globalContext = React.useContext(GlobalContext);
	if (globalContext == null) return null;
	const {licenseCache} = globalContext;
	const licenseRestrictionText = !licensePermissions.mayUseForCommercialPurposesAt({pageUrl: result.originUrl}) ?	
		UnlicensedText : undefined;
	const { requestGetter } = rest;
	return (<>{
		searchResults.map( (searchResult, _index) => {
			const key = ContentSourceSearchResultKey(searchResult);  // `SearchResultEventResultView.searchResults: ${result.eventIndex}:${searchResult.stringFound}:${enhancedContentSourceLocation(searchResult)}:${searchResult.position}`
			const hideDetails = !licenseCache.mayGenerateDetailedEvidenceAt({pageUrl: result.originUrl})
			return (
				<div className={rcss.SearchResultContainer} key={key} data-license={licenseRestrictionText} >
					<div  className={rcss.SearchResultFindingsContainer}>
						<MsSinceRecordingStartView msSinceRecordingStart={msSinceRecordingStart} />
						<span className={rcss.SearchTermFoundElement}>{ searchResult.searchTermAsSpecified }</span>
						&#32;{ searchResult.direction === 'sent' ? 'sent to' : 'received from' }&nbsp;
						<span className={rcss.DestinationFoundElement}>{ getRegisteredDomain(result.destinationUrl) }</span>
						&#32;
						{searchResult.encodingSteps.length === 0 ? null : (<>
							encoded as&#32;
							<Literal>{ describeEncodedSearchTermUsingFunctionNotationHtml({...searchResult, hideDetails})}</Literal>&#32;
						</>
						)}
						{
							("location" in searchResult && typeof searchResult.location === "string") ? (<>
									at&#32;
									<Literal>{enhancedContentSourceLocation(searchResult)}{ hideDetails ? '' : `:${searchResult.position}`}</Literal>
									&#32;
								</>) : null
						}
						of request&#32;
						{ hideDetails ? `${requestId}` : (
							<InteractiveRequestId {...{requestId, eventIndex, ...rest}} />
						)}
					</div>
					{ hideDetails ? (
						<div className={rcss.contextLine}>Sent from: <Literal>{result.originUrl}</Literal></div>
					) : (
						<details className={rcss.contextDetails}>
							<summary className={rcss.contextSummaryLine}>
								<span className={rcss.contextSpan}>{ searchResult.prefix }</span>
								<span className={rcss.emphasizedResultSpan}>{ searchResult.stringFound }</span>
								<span className={rcss.contextSpan}>{ searchResult.suffix }</span>
							</summary>
							{
								JSON.stringify(requestGetter?.get(requestId) ?? requestId, undefined, "  ")
							}
						</details>
					)}
				</div>
			)
		})}
	</>);
});


const MetaEvent = ({children, msSinceRecordingStart}: PropsWithChildren<{msSinceRecordingStart: number}>) => (
	<div className={rcss.MetaEventElement}>
		<MsSinceRecordingStartView msSinceRecordingStart={msSinceRecordingStart} />
		{children}
	</div>
)

export const DomEventFiredEventView = ({domEventFiredEvent, msSinceRecordingStart}: {
	domEventFiredEvent: DbRecordedEvent<InstrumentationEventDomEventFired>;
	msSinceRecordingStart: number;
}) => {
	const {raw} = domEventFiredEvent;
	const {elementId, elementName, elementUUID} = raw;
	const elementIdentifier = elementId?.length ? elementId :
		elementName?.length ? elementName :
		elementUUID ?? "";
	const forElementHtml = elementIdentifier.length === 0 ? null : (<> for <Literal>{elementIdentifier}</Literal></>);

	switch (domEventFiredEvent.raw.domOrCustomEventName) {
		case "input":
		case "change":
			if (domEventFiredEvent.raw.value != null) {
				return <MetaEvent {...{msSinceRecordingStart}}>User set input value{forElementHtml} to: <QuotedLiteral>{domEventFiredEvent.raw.value}</QuotedLiteral></MetaEvent>;
			}
			break;
		case "submit":
			return <MetaEvent {...{msSinceRecordingStart}}>Form submitted{forElementHtml}</MetaEvent>;
		case "click":
			return (
			<MetaEvent {...{msSinceRecordingStart}}>User clicked on {domEventFiredEvent.raw.elementType} element{
					elementIdentifier.length === 0 ? "" : (<> with id <Literal>elementIdentifier</Literal></>)
				}</MetaEvent>);
		case "focusin":
			return (
				<MetaEvent {...{msSinceRecordingStart}}>User focused on {domEventFiredEvent.raw.elementType} element{
					elementIdentifier.length === 0 ? ""  : (<>with id <Literal>elementIdentifier</Literal></>)
				}</MetaEvent>);
	}
}

export const TabEventView = ({tabEvent, msSinceRecordingStart}: {tabEvent: DbRecordedEvent<TabEvent>, msSinceRecordingStart: number;}) => {
	switch (tabEvent.eventName) {
		case 'activated':
			return <MetaEvent {...{msSinceRecordingStart}}>User (re-)activated tab <Literal>{tabEvent.tab.tabId}</Literal> ({tabEvent.tab.url})</MetaEvent>;
		case 'removed':
			return <MetaEvent {...{msSinceRecordingStart}}>User removed tab <Literal>{tabEvent.tab.tabId}</Literal> ({tabEvent.tab.url})</MetaEvent>;
		case 'startRecording':
			return <MetaEvent {...{msSinceRecordingStart}}>User started recording tab <Literal>{tabEvent.tab.tabId}</Literal> ({tabEvent.tab.url})</MetaEvent>;
		case 'stopRecording':
			return <MetaEvent {...{msSinceRecordingStart}}>User stopped recording tab <Literal>{tabEvent.tab.tabId}</Literal> ({tabEvent.tab.url})</MetaEvent>;
		case 'updated': {
			const { url } = tabEvent.raw;
			if (url != null) {
				return <MetaEvent {...{msSinceRecordingStart}}>Loaded page <Literal>{url}</Literal> in tab <Literal>{tabEvent.tab.tabId}</Literal></MetaEvent>;
			}
		}
			break;
		case 'created': {
			const { id, url } = tabEvent.raw;
			if (url != null) {
				return (<MetaEvent {...{msSinceRecordingStart}}>User created new tab <Literal>{id}</Literal> with initial URL <Literal>{url}</Literal></MetaEvent>);
			}
		}
			break;
		default:
			break;
	}
}

export const SearchResultsEventView = (args: InteractiveRequestIdBaseProps & HasLicensePermissions & {
	searchResultEvent: ChronologicalReportingEvent;
	msSinceRecordingStart: number;
}) => {
	const {
		searchResultEvent, msSinceRecordingStart, ...rest
	} = args;
	if (isDbNetworkEventWithSearchResults(searchResultEvent)) {
		return <SearchResultEventResultView {...{result: searchResultEvent, msSinceRecordingStart, ...rest}} />;
	} else if (isDomEventFiredEvent(searchResultEvent)) {
		return <DomEventFiredEventView domEventFiredEvent={searchResultEvent} {...{msSinceRecordingStart}} />;
	} else if (searchResultEvent.category === "Tabs") {
		return <TabEventView tabEvent={searchResultEvent} {...{msSinceRecordingStart}} />;
	}
}



/**
 * 
 * 
 * add a search term filter
 * 
 * 
 */
export const ChronologicalSearchResultsView = observer( ({
	chronologicalResultsObserver, startTimeIso, ...rest}: 
	InteractiveRequestIdBaseProps & HasLicensePermissions & {
		chronologicalResultsObserver: ChronologicalResultsObserver;
		startTimeIso?: ISODateTimeString;
	}) => {
	const recordingStartTimeMs = startTimeIso != null ? new Date(startTimeIso).getTime() :
		Math.min(Date.now(), ...chronologicalResultsObserver.chronologicalResults.map( (searchResultEvent) => new Date(searchResultEvent.whenRecordedIso).getTime()));
	return (
		<div className={rcss.SearchResultsContainer}>
			{chronologicalResultsObserver.chronologicalResults.map( (searchResultEvent) => {
				return <SearchResultsEventView
					key={`FilteredSearchResultsView.filteredChronologicalResults ${searchResultEvent.chronologicalEventIndex}`}
					searchResultEvent={searchResultEvent}
					msSinceRecordingStart={new Date(searchResultEvent.whenRecordedIso).getTime() - recordingStartTimeMs}
					{...rest}
				/>
			})}
		</div>
	);
})